import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import * as _ from '../lodash';
import Select from '../components/Common/Select';
import Table from '../components/Common/Table';
import Button from '../components/Common/Button';
import DatePicker from '../components/DatePicker';
import Alert from '../components/Alert';
import Spinner from '../components/Spinner';
import { parseInt, capitalize, formatAvailabilityMessage } from '../utilities/helperUtility';
import { SHORT_DATE_FORMAT } from '../constants';
import { formatHours, formatPrice, getTimeFrameFromDate } from '../utilities/timeUtility';
import Translation from '../utilities/localizationUtility';
import ExceededMessage from "../components/exceeded_free_trial_message.js"

const firstSelectableDate = (frames, availableDates) => {
  const dates = frames.map(x => x.date);
  const today = new Date();
  let firstBookableDate = _.first(_.filter(dates, x => moment(x).diff(today, 'days') >= 0));
  if (firstBookableDate) {
    if (!availableDates.includes(firstBookableDate)) {
      firstBookableDate = availableDates[0];
    }
    return firstBookableDate;
  }
  return null;
};

const parseAvailableDates = (frames, totalQuantity, minDate, maxDate, timeZone) => {
  const today = moment(new Date()).tz(timeZone);
  return frames.reduce((arr, frame) => {
    const theDate = moment(frame.date);
    let isValid = false;
    if (theDate.isSameOrAfter(moment().startOf('day'))) {
      if (minDate) {
        if (theDate.isSameOrAfter(today.clone().add(minDate, 'seconds'))) {
          if (maxDate) {
            if (
              today
                .clone()
                .add(maxDate, 'days')
                .isSameOrAfter(theDate)
            ) {
              isValid = true;
            }
          } else {
            isValid = true;
          }
        }
      } else if (maxDate) {
        if (
          today
            .clone()
            .add(maxDate, 'days')
            .isSameOrAfter(theDate)
        ) {
          isValid = true;
        }
      } else {
        isValid = true;
      }
    }
    if (isValid && _.find(frame.timeslots, x => moment(x.start).isSameOrAfter(today))) {
      arr.push(frame.date);
    }
    return arr;
  }, []);
};

const parseSelectOptions = (frames, selectedDate, maxAvailability, totalQuantity, capacityType, locale, timeZone) => {
  if (selectedDate) {
    const now = moment();
    const timeslots = _.filter(_.find(frames, x => x.date === selectedDate)
      .timeslots, x => moment(x.start).isSameOrAfter(now))
      .sort((x, y) => moment(y.start).isSameOrAfter(moment(x.start)) ? -1 : 1);

    return timeslots.map((x) => {
      const availability = capacityType == 2 ? x.resource_availability : maxAvailability - _.sumBy(x.availability, 'bookings');

      const message = formatAvailabilityMessage(availability, totalQuantity);

      return {
        label: `${formatHours(x.start, locale, timeZone)} - ${formatHours(x.finish, locale, timeZone)} / ${message}`,
        value: x.start,
      };
    });
  }
  return [];
};

export default class ActivityPage extends Component {
  state = {
    currentMonth: null,
    availableDates: [],
    selectOptions: [],
    width: window.innerWidth,
  };

  updateDimensions = this.updateDimensions.bind(this);

  onMonthChange = this.onMonthChange.bind(this);

  onDayClick = this.onDayClick.bind(this);

  dayDisabled = this.dayDisabled.bind(this);

  fakeDisabled = this.fakeDisabled.bind(this);

  goNextMonthButton = this.goNextMonthButton.bind(this);

  onSelectChange = this.onSelectChange.bind(this);

  UNSAFE_componentWillReceiveProps(newProps) {
    const {
      frames,
      capacityType,
      totalQuantity,
      selectedDate,
      locale,
      timeZone,
      maxAvailability,
      minDate,
      maxDate,
      orderActions: { setOrderDetails },
      exceededTrialPlan
    } = newProps;

    const { currentMonth } = this.state;

    if (frames.length > 0) {
      const availableDates = parseAvailableDates(frames, totalQuantity, minDate, maxDate, timeZone);
      const selectOptions = parseSelectOptions(frames, selectedDate, maxAvailability, totalQuantity, capacityType, locale, timeZone);

      if (!selectedDate && !currentMonth) {
        const firstBookableDate = firstSelectableDate(frames, availableDates);
        if (firstBookableDate) {
          setOrderDetails({ date: firstBookableDate });

          const today = moment().format('YYYY-MM');
          const cm = moment(firstBookableDate).format('YYYY-MM');
          if (today === cm) {
            // if this month is same with first bookable date's month, doesn't load frames.
            this.setState({
              currentMonth: moment(firstBookableDate).format(SHORT_DATE_FORMAT),
            });
          } else {
            // if this month is different with first bookable date's month, go to first bookable date's month.
            this.onMonthChange(firstBookableDate);
          }
        }
      } else {
        this.setState({
          availableDates,
          selectOptions,
        });
      }
    } else {
      this.setState({
        availableDates: [],
        selectOptions: [],
      });
    }
  }

  componentDidMount() {
    window.addEventListener('resize', this.updateDimensions);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions);
  }

  updateDimensions() {
    this.setState({
      width: window.innerWidth,
    });
  }

  onSelectVariant(name, value) {
    const {
      orderActions: { setVariantsQuantity },
    } = this.props;
    setVariantsQuantity(name, value);
  }

  onDayClick(day, modifiers = {}) {
    if (modifiers.disabled) return;
    const {
      orderActions: { setOrderDetails },
      timeZone,
      frames,
      capacityType,
      selectedDate,
      totalQuantity,
      maxAvailability,
      locale,
    } = this.props;
    const newSelection = moment(day).format(SHORT_DATE_FORMAT);
    if (newSelection !== selectedDate) {
      setOrderDetails({
        date: newSelection,
        selectedTimeslot: null,
      });
    }

    const selectOptions = parseSelectOptions(frames, newSelection, maxAvailability, totalQuantity, capacityType, locale, timeZone);
    this.setState({
      selectOptions,
    });
  }

  onSelectChange(value) {
    const {
      orderActions: { setOrderDetails },
      frames,
      maxAvailability,
      selectedDate,
    } = this.props;
    if (value && selectedDate) {
      const frame = frames.find(x => x.date === selectedDate);
      const timeslot = frame.timeslots.find(x => x.start === value);
      if (timeslot) {
        const bookingCount = _.sumBy(timeslot.availability, 'bookings');
        const available = this.props.capacityType == 2 ? timeslot.resource_availability : maxAvailability - bookingCount;

        setOrderDetails({
          date: frame.date,
          available: available,
          selectedTimeslot: value,
        });
      }
    }
  }

  onSubmit(e) {
    const {
      selectedDate, selectedTimeslot, totalQuantity, orderActions, cartActions,
    } = this.props;
    const ele = e.target;
    const messages = [];

    if (totalQuantity <= 0) {
      messages.push('PLEASE SELECT A VARIANT');
    } else if (!selectedTimeslot) {
      messages.push('PLEASE SELECT A OPTION');
    } else if (!selectedDate) {
      messages.push('PLEASE SELECT A DATE');
    }

    if (!messages.length) {
      ele.disabled = true;
      orderActions
        .submitOrder()
        .then((response) => {
          const { reservation } = response.data;
          if (reservation.items.some(x => x.status !== 'ok')) {
            let m = 'SCHEDULE CONFLICT PLEASE RELOAD PAGE';

            if (window.screen.width < 380) m = 'PLEASE RELOAD PAGE';
            this.alert.show('red', [m]);
            ele.innerText = m;
          } else {
            cartActions.addItems(reservation, ['Date', 'Time']);
          }
        })
        .catch((responsed) => {
          ele.disabled = false;
          const { response } = responsed;
          if (response && response.data) {
            const { data } = response;

            Object.keys(data).forEach((key) => {
              const name = key
                .split('_')
                .map(x => capitalize(x))
                .join('');
              messages.push(`${name} ${data[key]}`);
            });

            this.alert.show('red', ['SERVER ERROR']);
          } else {
            this.alert.show('red', ['SOMETHING WRONG']);
            throw responsed;
          }
        });
    } else {
      this.alert.show('red', messages);
    }
  }

  onMonthChange(value) {
    const {
      frameActions: { fetchFrames },
      exceededTrialPlan,
      timeZone,
    } = this.props;
    this.setState(
      {
        currentMonth: moment(value).format(SHORT_DATE_FORMAT),
      },
      () => {
        if (!exceededTrialPlan) {
          const { start, finish } = getTimeFrameFromDate(moment(value).format(SHORT_DATE_FORMAT), timeZone);
          fetchFrames({ start: start.format(), finish: finish.format() });
        }
      },
    );
  }

  onlyDefaultTitle() {
    const { variants } = this.props;
    return variants.length === 1 && variants[0].title === 'Default Title';
  }

  dayDisabled(value) {
    const day = moment(value).format(SHORT_DATE_FORMAT);
    const { availableDates } = this.state;
    if (!availableDates.includes(day)) {
      return new Date(day);
    }
    return null;
  }

  fakeDisabled(value) {
    const { frames } = this.props;
    const day = moment(value).format(SHORT_DATE_FORMAT);
    if (frames.some(frame => frame.date === day)) {
      return new Date(day);
    }
    return null;
  }

  columns() {
    const {
      variants, productOptions, translation, defaultTranslation,
    } = this.props;
    const priceSymbol = translation.priceSymbol || defaultTranslation.priceSymbol;
    const onlyDefaultTitle = this.onlyDefaultTitle();
    const headers = productOptions ? productOptions.map(x => x.name) : [];
    if (!onlyDefaultTitle) {
      headers.push('Price');
    }

    // prepare the list from variant.options and according headers ordering
    const lists = variants
      && variants.map((variant) => {
        const hash = { key: variant.external_id };
        const extra = Array.from(headers).slice(variant.options.length);

        variant.options.filter(x => x).forEach((option, index) => (hash[headers[index]] = option));

        extra.map(x => (hash[x] = formatPrice(variant[x.toLowerCase()], priceSymbol)));
        return hash;
      });

    if (onlyDefaultTitle) lists[0].Title = 'Quantity';

    return { headers, lists };
  }

  extraColumns() {
    const {
      spinner,
      props: { variants, selectedTimeslot, frames },
    } = this;
    const attrs = {};
    variants.forEach((variant) => {
      attrs[variant.external_id] = this.onlyDefaultTitle() ? { style: { float: 'right' } } : {};

      if (selectedTimeslot) {
        const { available } = frames
          .reduce((all, _frame) => all.concat(_frame.timeslots), [])
          .find(slot => slot.start === selectedTimeslot)
          .availability.find(ava => ava.external_id === variant.external_id);

        attrs[variant.external_id] = { ...attrs[variant.external_id], max: available };
      }
    });

    return [
      {
        attrs,
        header: 'quantity',
        type: 'number',
        onChange: ({ target: { name, value } }) => {
          if (spinner) spinner.show();
          this.onSelectVariant(name, parseInt(value, 10));
        },
      },
    ];
  }

  goNextMonthButton() {
    const {
      props: { nextOccurrence },
    } = this;
    if (nextOccurrence) {
      this.setState({
        currentMonth: moment(nextOccurrence).format(SHORT_DATE_FORMAT),
      });
    }
  }

  render() {
    const {
      timeZone,
      lastOccurrence,
      nextOccurrence,
      totalPrice,
      frames,
      selectedTimeslot,
      variants,
      region,
      selectedDate,
      totalQuantity,
      available,
      orderVariants,
      style,
      exceededTrialPlan
    } = this.props;

    const { currentMonth, selectOptions, width } = this.state,
          firstOccurrence = _.min(frames.map(x => moment(x.date))),
          selectedDateObj = selectedDate ? moment(selectedDate).toDate() : null;

    let showGoNextMonthButton = false;

    if (currentMonth) {
      const date = moment(currentMonth);
      showGoNextMonthButton = nextOccurrence
        && frames.filter(x => moment(x.date).format('YYYY-MM') === date.format('YYYY-MM')).length === 0
        && date.isBefore(moment(nextOccurrence));
    }

    const canBook = available >= totalQuantity && frames.length && variants.length,
          t = new Translation(this.props),
          submitText = canBook ? t.key('submit') : t.key('noAvailability'),
          defaultWidth = '450px',
          isLargeScreen = width > 800,
          wrapperStyle = { ...style.body, minHeight: '420px' };

    if (!isLargeScreen) {
      wrapperStyle.maxWidth = defaultWidth;
    }

    return (
      <div className="text-center center" style={wrapperStyle}>
        <div className="sub-panel">
          <h3 className="text-left" style={{ marginTop: '0' }}>
            {t.key('title')}
          </h3>

          {exceededTrialPlan &&
            <div className="text-left">
              <Alert unclosed={true} messages={[<ExceededMessage/>]} type="exceeded-trial-plan" />
            </div>
          }

          {variants && (
            <div style={{ margin: '-10px 10px', maxWidth: defaultWidth }}>
              <Table
                className="table-component"
                hideHead={this.onlyDefaultTitle()}
                style={style.variantsTable}
                items={orderVariants}
                columns={this.columns()}
                extraColumns={this.extraColumns()}
              />
            </div>
          )}

          <div className="relative">
            <Spinner ref={ref => (this.spinner = ref)} />

            <DatePicker
              fixedWeeks
              styled={style.datepicker}
              region={region}
              className={isLargeScreen ? 'DayPicker--moved-left' : ''}
              fromMonth={moment(firstOccurrence).toDate()}
              toMonth={moment(lastOccurrence).toDate()}
              month={moment(currentMonth || new Date()).toDate()}
              onMonthChange={this.onMonthChange}
              onDayClick={this.onDayClick}
              modifiers={{
                selected: selectedDateObj,
                disabled: this.dayDisabled,
                fakeDisabled: this.fakeDisabled,
              }}
            />

            <div className={isLargeScreen ? 'Select--moved-right' : ''}>
              {showGoNextMonthButton ? (
                <Button
                  className="go-new-month"
                  reverse
                  styled={style.submit}
                  style={{
                    fontSize: '14px',
                    paddingTop: '9px',
                    paddingBottom: '9px',
                  }}
                  onClick={this.goNextMonthButton}
                >
                  {t.key('goNextMonth')}
                </Button>
              ) : (
                <Select
                  className="Select-dropup"
                  isClearable={false}
                  styled={style.select}
                  isSearchable={false}
                  backspaceRemovesValue={false}
                  value={selectedTimeslot}
                  options={selectOptions}
                  onChange={this.onSelectChange}
                />
              )}

              <p className="text-right timezone" style={{ fontSize: '11px', margin: '5px 5px 0' }}>
                {timeZone}
                {' Timezone'}
              </p>
              <p className="text-right">
                Price:
                <span className="bold ml10" style={{ ...style.price, fontSize: '35px' }}>
                {t.key('priceSymbol')}
                    {(+totalPrice).toFixed(2)}
                </span>
              </p>
              <Alert ref={ref => (this.alert = ref)} />
            </div>

          </div>

        </div>
        <Button
          styled={style.submit}
          disabled={!canBook}
          className="submit ptb15"
          onClick={e => this.onSubmit(e)}>
          {submitText}
        </Button>
      </div>
    );
  }
}

ActivityPage.propTypes = {
  translation: PropTypes.object,
  defaultTranslation: PropTypes.object,
  timeZone: PropTypes.string,
  productOptions: PropTypes.arrayOf(PropTypes.shape({
    position: PropTypes.number,
    name: PropTypes.string,
    values: PropTypes.array,
  })),
  variants: PropTypes.array,
  available: PropTypes.number,
  frames: PropTypes.array,
  totalPrice: PropTypes.number,
  totalQuantity: PropTypes.number,
  nextOccurrence: PropTypes.string,
  lastOccurrence: PropTypes.instanceOf(Date),
  selectedDate: PropTypes.string,
  selectedTimeslot: PropTypes.string,
  orderActions: PropTypes.shape({
    setVariantsQuantity: PropTypes.func,
    setSelectedTimeslot: PropTypes.func,
    setOrderDetails: PropTypes.func,
  }),
  cartActions: PropTypes.shape({
    addItems: PropTypes.func,
  }),
  frameActions: PropTypes.shape({ fetchFrames: PropTypes.func }),
  orderVariants: PropTypes.shape({
    external_id: PropTypes.string,
    quantity: PropTypes.number,
    start: PropTypes.instanceOf(Date),
  }),
  style: PropTypes.object,
};
